import { SigningAdapter, type SignL1ActionParams } from './base-adapter';
import { EIP712_DOMAINS, EIP712_TYPES } from '../core/constants';

export interface PrivyRPCAdapterConfig {
  appId: string;
  appSecret: string;
  walletId: string; // Privy wallet ID from delegated wallet
  walletAddress: string; // Ethereum wallet address
}

/**
 * Signing adapter for Node.js backend using Privy's RPC API
 */
export class PrivyRPCAdapter extends SigningAdapter {
  private config: PrivyRPCAdapterConfig;
  private baseUrl = 'https://api.privy.io/v1';

  constructor(config: PrivyRPCAdapterConfig) {
    super();
    this.config = config;
  }

  async signL1Action(params: SignL1ActionParams): Promise<`0x${string}`> {
    const url = `${this.baseUrl}/wallets/${this.config.walletId}/rpc`;

    const authHeader = Buffer.from(
      `${this.config.appId}:${this.config.appSecret}`
    ).toString('base64');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${authHeader}`,
        'privy-app-id': this.config.appId,
      },
      body: JSON.stringify({
        method: 'eth_signTypedData_v4',
        params: {
          typed_data: {
            domain: EIP712_DOMAINS.EXCHANGE,
            types: EIP712_TYPES.AGENT,
            message: {
              source: params.isTestnet ? 'b' : 'a',
              connectionId: params.actionHash,
            },
            primary_type: 'Agent',
          },
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'Unknown error',
      }));
      throw new Error(`Privy RPC signing failed: ${JSON.stringify(error)}`);
    }

    const result = (await response.json()) as {
      data: { signature: string };
    };
    return result.data.signature as `0x${string}`;
  }

  async isReady(): Promise<boolean> {
    return !!(
      this.config.appId &&
      this.config.appSecret &&
      this.config.walletId &&
      this.config.walletAddress
    );
  }

  getWalletAddress(): string {
    return this.config.walletAddress;
  }
}
